import { DragEndEvent, DragOverEvent } from '@dnd-kit/core';
import { SortableContext } from '@dnd-kit/sortable';
import * as Lodash from 'lodash';
import { useCallback, useEffect } from 'react';
import { DragAndDropFuncs } from '../../../../common/core/drag-and-drop/drag-and-drop.funcs';
import { ImmerFuncs } from '../../../../external/immer/immer.funcs';
import { ServerApi } from '../../../../external/server/server.api';
import { ArrayFuncs } from '../../../../foundation/js/array.funcs';
import { JsFuncs } from '../../../../foundation/js/js.funcs';
import { GapY } from '../../../../foundation/layout/gap-y';
import { StackY } from '../../../../foundation/layout/stack-y';
import { MegaBoardContentWire } from '../../../core/board/mega/mega-board-content.wire';
import { MegaBoardContentWireFuncs } from '../../../core/board/mega/mega-board-content.wire.funcs';
import { NodeStore } from '../../../core/node/node.store';
import { CentralDragAndDropStore } from '../../app/drag-and-drop/central-drag-and-drop.store';
import { MegaBoardRow } from './mega-board-row';
import { MegaBoardStyle } from './mega-board.style';

export function MegaBoard({ boardId }: { boardId: string }) {
  const board = NodeStore.useItem(boardId);

  useEffect(() => {
    (async () => {
      const nodes = await ServerApi.getNodesAndAllSubNodes([boardId]);
      NodeStore.updateItems(Lodash.keyBy(nodes, 'id'));
    })();
  }, [boardId]);

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLDivElement>) => {
      const text = e.currentTarget.innerText;
      NodeStore.updateItem(boardId, { text });
      ServerApi.updateNode(boardId, { text });
    },
    [boardId],
  );

  const rows = ArrayFuncs.ensure(board?.sub?.items);

  // for rows
  useEffect(() => {
    function handleDragOver(e: DragOverEvent) {
      const startingId = e.active.id as string;
      const endingId = e.over?.id as string;

      if (startingId === endingId) {
        return;
      }

      const board = NodeStore.getItem(boardId);

      const sub = ImmerFuncs.produce(board?.sub as MegaBoardContentWire, (draft) => {
        const items = ArrayFuncs.ensure(draft.items);
        const newItems = DragAndDropFuncs.moveItemsInArray({
          items,
          itemIdMapper: (item) => item.id,
          starting: startingId,
          ending: endingId,
        });
        draft.items = newItems;
      });

      NodeStore.updateItem(boardId, { sub });
    }

    function handleDragEnd(e: DragEndEvent) {
      const board = NodeStore.getItem(boardId);
      ServerApi.updateNode(boardId, { sub: board?.sub });
    }

    CentralDragAndDropStore.register({ type: 'mega-board-row', handler: { onDragOver: handleDragOver, onDragEnd: handleDragEnd } });
  }, [boardId]);

  // for cols
  useEffect(() => {
    function handleDragOver(e: DragOverEvent) {
      const startingId = e.active.id as string;
      const endingId = e.over?.id as string;

      if (startingId === endingId) {
        return;
      }

      if (JsFuncs.not(e.over?.data.current?.type === 'mega-board-col')) {
        return;
      }

      const board = NodeStore.getItem(boardId);

      const sub = ImmerFuncs.produce(board?.sub as MegaBoardContentWire, (draft) => {
        MegaBoardContentWireFuncs.moveCol(draft, { startingColId: startingId, endingColId: endingId });
      });

      NodeStore.updateItem(boardId, { sub });
    }

    function handleDragEnd(e: DragEndEvent) {
      const board = NodeStore.getItem(boardId);
      ServerApi.updateNode(boardId, { sub: board?.sub });
    }

    CentralDragAndDropStore.register({ type: 'mega-board-col', handler: { onDragOver: handleDragOver, onDragEnd: handleDragEnd } });
  }, [boardId]);

  return (
    <MegaBoardStyle.Container>
      <MegaBoardStyle.Title contentEditable={true} onBlur={handleBlur} dangerouslySetInnerHTML={{ __html: board?.text! }}></MegaBoardStyle.Title>
      <GapY $gap="40px" />
      <StackY $gap="40px">
        <SortableContext items={rows.map((row) => row.id)}>
          {rows.map((row) => (
            <MegaBoardRow key={row.id} row={row} />
          ))}
        </SortableContext>
        <MegaBoardRow key={rows.length} />
      </StackY>
    </MegaBoardStyle.Container>
  );
}
